import { Box, Checkbox, Skeleton, Table, Text } from "@mantine/core";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../../apis";
import { IJournalResponse, IPeriodBalanceRow, ITrialBalanceReport } from "../../../apis/ledger";
import { message } from "../../../utils/Message";
import { Amt, money } from "./report-format";
import { saveBlob } from "./report-download";
import { periodBounds, periodKey, useReportPeriod } from "./report-period";
import { ReportShell, StatusPill } from "./ReportShell";
import classes from "./ReportShell.module.css";
import { ListStates, resolveListStates } from "../../../components/list";

const COLS = 6;

const hasActivity = (r: IPeriodBalanceRow) =>
  Number(r.openingBalance) !== 0 || Number(r.periodDebit) !== 0 || Number(r.periodCredit) !== 0 || Number(r.closingBalance) !== 0;

/** Тэнцлийн үлдэгдлийн тайлан (SPRINT-05 §6.1) — данс бүрийн эхний үлдэгдэл, эргэлт, эцсийн үлдэгдэл. */
export const LedgerTrialBalancePage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const navigate = useNavigate();
  const [period, setPeriod] = useReportPeriod();
  const [params, setParams] = useSearchParams();
  const [exporting, setExporting] = useState(false);
  const hideZero = params.get("zero") !== "show";

  const setHideZero = useCallback(
    (hide: boolean) => {
      const next = new URLSearchParams(params);
      if (hide) next.delete("zero");
      else next.set("zero", "show");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const { data, isLoading, error } = useSwr<ITrialBalanceReport>(
    `ledger.report.trialBalance.${periodKey(period)}`,
    async () => await LedgerApi.getTrialBalance(period),
    { revalidateOnFocus: false },
  );

  // Бичилтийн тоо: данс бүрийн entryCount-ийн нийлбэр нь бичилтийг давхар тоолно
  // (нэг бичилт ≥ 2 дансанд), тиймээс тухайн сарын бичилтийн тоог журналаас авна.
  const bounds = periodBounds(period);
  const { data: journal } = useSwr<IJournalResponse>(
    `ledger.report.trialBalance.entries.${periodKey(period)}`,
    async () => await LedgerApi.getJournal({ page: 1, limit: 1, dateFrom: bounds.from, dateTo: bounds.to }),
    { revalidateOnFocus: false },
  );

  const allRows = useMemo(() => data?.rows ?? [], [data]);
  const rows = useMemo(() => (hideZero ? allRows.filter(hasActivity) : allRows), [allRows, hideZero]);
  const hidden = allRows.length - allRows.filter(hasActivity).length;

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      saveBlob(await LedgerApi.exportReportExcel({ report: "trial-balance", ...period }), `trial-balance-${periodKey(period)}.xlsx`);
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const openStatement = (row: IPeriodBalanceRow) =>
    navigate(`/ledger/reports/account-statement?accountId=${row.accountId}&dateFrom=${bounds.from}&dateTo=${bounds.to}`);

  const ready = !!data && allRows.length > 0;

  const listStates = resolveListStates(
    <ListStates loading={isLoading} empty={rows.length === 0} columns={COLS} />
  );

  return (
    <ReportShell
      title={t("billing:ledger.report.trialBalanceTitle")}
      status={
        ready && (
          <StatusPill ok={data.isBalanced}>
            {data.isBalanced
              ? t("billing:ledger.balanced")
              : t("billing:ledger.report.unbalancedBy", { amount: money(Math.abs(data.difference)) })}
          </StatusPill>
        )
      }
      subtitle={t("billing:ledger.report.trialBalanceSubtitle", {
        year: period.year,
        month: t(`billing:ledger.report.month.${period.month}`),
      })}
      error={error}
      period={period}
      onPeriodChange={setPeriod}
      onExport={{ onExcel: handleExportExcel, excelLoading: exporting }}
      controls={
        <Checkbox
          size="sm"
          checked={hideZero}
          onChange={(e) => setHideZero(e.currentTarget.checked)}
          label={t("billing:ledger.report.hideZero")}
        />
      }
    >
      <Box className={classes.card}>
        <Box className={classes.wrap}>
          {listStates ?? (
          <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("billing:ledger.colAccount")}</Table.Th>
                <Table.Th className={classes.num}>{t("billing:ledger.report.colOpening")}</Table.Th>
                <Table.Th className={classes.num}>{t("billing:ledger.colDebit")}</Table.Th>
                <Table.Th className={classes.num}>{t("billing:ledger.colCredit")}</Table.Th>
                <Table.Th className={classes.num}>{t("billing:ledger.report.colClosing")}</Table.Th>
                <Table.Th className={classes.num}>{t("billing:ledger.report.colEntries")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                  <Table.Tr
                    key={row.accountId}
                    className={classes.clickRow}
                    tabIndex={0}
                    role="link"
                    aria-label={`${row.code} ${row.name}`}
                    onClick={() => openStatement(row)}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter") openStatement(row);
                    }}
                  >
                    <Table.Td>
                      <Box className={classes.stack}>
                        <Text span className={classes.name}>{row.name}</Text>
                        <Text span className={classes.code}>
                          {row.code} · {t(`billing:ledger.type.${row.accountType}`, { defaultValue: row.accountType })}
                        </Text>
                      </Box>
                    </Table.Td>
                    <Table.Td className={classes.num}><Amt value={row.openingBalance} /></Table.Td>
                    <Table.Td className={classes.num}><Amt value={row.periodDebit} /></Table.Td>
                    <Table.Td className={classes.num}><Amt value={row.periodCredit} /></Table.Td>
                    <Table.Td className={classes.num}>
                      {Number(row.closingBalance) === 0 ? <Amt value={0} /> : <Text span className={classes.strong}>{money(row.closingBalance)}</Text>}
                    </Table.Td>
                    <Table.Td className={classes.num}>
                      {row.entryCount > 0 ? row.entryCount : <Text span className={classes.muted}>0</Text>}
                    </Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
            {ready && rows.length > 0 && (
              <Table.Tfoot>
                <Table.Tr className={classes.totalRow}>
                  <Table.Td>{t("billing:ledger.report.totalTurnover")}</Table.Td>
                  <Table.Td />
                  <Table.Td className={`${classes.num} ${classes.strong}`}>{money(data.totalDebit)}</Table.Td>
                  <Table.Td className={`${classes.num} ${classes.strong}`}>{money(data.totalCredit)}</Table.Td>
                  <Table.Td />
                  <Table.Td className={`${classes.num} ${classes.strong}`}>{journal ? journal.summary.entries : "-"}</Table.Td>
                </Table.Tr>
              </Table.Tfoot>
            )}
          </Table>
          )}
        </Box>
        {ready && hideZero && hidden > 0 && (
          <Box className={classes.cardFoot}>
            {t("billing:ledger.report.hiddenZero", { count: hidden })}
            {" · "}
            <Box component="button" type="button" className={classes.linkBtn} onClick={() => setHideZero(false)}>
              {t("billing:ledger.report.showAll")}
            </Box>
          </Box>
        )}
      </Box>
    </ReportShell>
  );
};

export default LedgerTrialBalancePage;
